import { useEffect, useRef } from "react";
import { getAccountPrivacy, updateAccountPrivacy } from "@/api/privacy";
import {
  confirmAnalyticsConsent,
  getPrivacyConsent,
  getPrivacyAccount,
  hasDeviceAnalyticsChoice,
  privacySyncFailed,
  usePrivacyConsent,
} from "@/lib/privacy-consent";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";

let pending = Promise.resolve();

/** Account consent is read before a stored local grant can be used. */
export function usePrivacyConsentSync(userId: string | null): void {
  const { analytics, revision } = usePrivacyConsent();
  const initialChoice = useRef<{ userId: string; revision: number } | null>(
    null,
  );
  useEffect(() => {
    if (!userId) {
      initialChoice.current = null;
      return;
    }
    if (initialChoice.current?.userId !== userId)
      initialChoice.current = { userId, revision };
    const first = initialChoice.current.revision === revision;
    let cancelled = false;
    pending = pending.then(async () => {
      if (
        cancelled ||
        getPrivacyAccount() !== userId ||
        getPrivacyConsent().revision !== revision
      )
        return;
      // An undecided device only reads the account choice; it never sends one.
      const chosen = hasDeviceAnalyticsChoice();
      try {
        let result =
          first || !chosen
            ? await getAccountPrivacy()
            : await updateAccountPrivacy({ analytics });
        if (
          cancelled ||
          getPrivacyAccount() !== userId ||
          getPrivacyConsent().revision !== revision
        )
          return;
        // Only an explicit device decline withdraws an account grant.
        if (first && chosen && !analytics && result.analytics) {
          result = await updateAccountPrivacy({ analytics: false });
        }
        if (!cancelled && getPrivacyConsent().revision === revision) {
          confirmAnalyticsConsent(userId, result.analytics === true);
        }
      } catch {
        if (
          !cancelled &&
          getPrivacyAccount() === userId &&
          getPrivacyConsent().revision === revision
        )
          privacySyncFailed(translate(libNoticesMessages, "privacySyncFailed"));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [analytics, revision, userId]);
}
